import "./driver-create-form.scss";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BasicModal, Button, Step, Stepper } from "@life-line-lab/components-react";
import { find, isEmpty, isUndefined, omitBy, isNil } from "lodash";
import errorHandler from "Utils/error-handler";
import { CloseCircleOutlined, DeleteFilled, UploadOutlined } from "@ant-design/icons";
import { Alert } from "antd";
import Attachments from "./stepperContents/attachments/attachments";
import DriverInfo from "./stepperContents/driver-info/driver-info";
import EmergencyContacts from "./stepperContents/emergency-contacts/emergency-contacts";
import PaymentDetails from "./stepperContents/payment-details/payment-details";
import VehicleInfo from "./stepperContents/vehicle-info/vehicle-info";
import AttachmentsSchema from "./stepperContents/attachments/attachments.validation";
import useCountriesList from "Hooks/countries-option.hook";
import useStatesList from "Hooks/states-option.hook";
import useDriverCreateForm, { DriverCreateFormProps } from "./use-driver-create-form";
import Upload from "Components/shared/upload/upload";
import { generateSteps } from "Helpers/generate-stepper-steps";
import { DriverApprovalStatus } from "Types/new_driver_types";

const DriverCreateForm: React.FC<DriverCreateFormProps> = ({
  onBack,
  formType,
  onSubmitEdit,
  onSubmit,
  onSubmitExternal,
  driverDetails,
  isSubmitLoading,
}) => {
  const { t } = useTranslation();

  const {
    initialUploadModal,
    validationSchemas,
    dataTableDetails,
    editFormType,
    input,
    setInput,
    prevImg,
    setPrevImg,
    error,
    setError,
    modal,
    setModal,
    uploadModal,
    setUploadModal,
    uploadFileList,
    setUploadFileList,
    isShowAlert,
    setIsShowAlert,
    currentFileUploadType,
    setCurrentFileUploadType,
    avatarImg,
    setAvatarImg,
    isLoading,
    setIsLoading,
    steps,
    setSteps,
    current,
    setCurrent,
    handleFieldChange,
    handleRadioChange,
    handleCheckboxChange,
    handlePhoneChange,
    handleAvatarClick,
    handleSelectChange,
    handleShowFileCompatibilityAlert,
    handleHideFileCompatibilityAlert,
    handleFileChange,
    handleCancelUpload,
    handleShowUploadModal,
    handleDeleteFile,
    handleFileRemove,
    handleConfirmFile,
    prev,
    next,
  } = useDriverCreateForm();

  // COUNTRIES, STATES OPTIONS
  const stateOption = useStatesList(input.country);
  const countryOption = useCountriesList();

  // COMMON PROPS FOR STEPPER CONTENTS
  const commonStepperProps = {
    input: input,
    error: error,
  };

  useEffect(() => {
    if (formType && editFormType.includes(formType)) {
      setCurrent(editFormType.indexOf(formType));
    }
  }, [formType]);

  // IF DRIVER EXIST, MAP DRIVER DETAILS INTO INPUT
  useEffect(() => {
    if (!isEmpty(driverDetails)) {
      setInput((prevInput) => ({
        ...prevInput,
        ...driverDetails,
      }));

      // Display steps based on driver status'
      const stepAmount: number = (driverDetails.status === 'PENDING_ACCOUNT' 
        && driverDetails.approvalStatus === DriverApprovalStatus.PENDING) ? 1 : 4

      setSteps && setSteps(generateSteps(stepAmount));
    }
  }, [driverDetails]);

  /**
   * function to submit driver creation form
   */
  const handleSubmit = () => {
    try {
      const value = AttachmentsSchema.validateSync(
        omitBy(
          {
            ...input,
          },
          isUndefined,
        ),
        {
          strict: true,
          abortEarly: true,
          stripUnknown: false,
        },
      );

      if (!isEmpty(value)) {
        const payload: any = {
          ...value,
        };
        if (onSubmit) onSubmit(payload);
        if (onSubmitExternal) onSubmitExternal(payload);
      } else {
        setError({ ...error, general: t("error.validationFailed") });
      }
    } catch (err) {
      let { path, value }: any = errorHandler.validation(err);
      // console.log(path, value)
      // e.g. contactNumber.number = contactNumber
      if (path.indexOf(".") !== -1) {
        const str = path.split(".");
        path = str[0];
      }

      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });

      if (document.querySelector(`input[name=${path}]`)) {
        (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
      } else if (document.querySelector(`div[name=${path}]`)) {
        (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
      }
    }
  };

  /**
   * function to update driver info
   */
  const handleUpdateSubmit = () => {
    try {
      const value = validationSchemas[current].validateSync(
        omitBy(
          {
            ...input,
          },
          isUndefined,
        ),
        {
          strict: true,
          abortEarly: true,
          stripUnknown: false,
        },
      );

      if (!isEmpty(value)) {
        const payload: any = {
          ...value,
        };
        if (onSubmitEdit) onSubmitEdit(payload);
      } else {
        setError({ ...error, general: t("error.validationFailed") });
      }
    } catch (err) {
      let { path, value }: any = errorHandler.validation(err);
      console.log(path, value);
      // e.g. contactNumber.number = contactNumber
      if (path.indexOf(".") !== -1) {
        const str = path.split(".");
        path = str[0];
      }

      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });

      if (document.querySelector(`input[name=${path}]`)) {
        (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
      } else if (document.querySelector(`div[name=${path}]`)) {
        (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
      }
    }
  };

  // stepper components list
  const stepperContents = [
    <DriverInfo
      {...commonStepperProps}
      handleFieldChange={handleFieldChange}
      handleRadioChange={handleRadioChange}
      handleAvatarClick={handleAvatarClick}
      handleSelectChange={handleSelectChange}
      handlePhoneChange={handlePhoneChange}
      countryOption={countryOption}
      stateOption={stateOption}
    />,
    <VehicleInfo
      {...commonStepperProps}
      setInput={setInput}
      handleFieldChange={handleFieldChange}
      handleRadioChange={handleRadioChange}
      handleSelectChange={handleSelectChange}
      isExternalCreation={onSubmitExternal ? true : false}
    />,
    <PaymentDetails
      {...commonStepperProps}
      handleFieldChange={handleFieldChange}
      handleSelectChange={handleSelectChange}
    />,
    <EmergencyContacts
      {...commonStepperProps}
      handleFieldChange={handleFieldChange}
      handlePhoneChange={handlePhoneChange}
    />,
  ];

  const extendedStepperContents = !input?._id
    ? stepperContents.concat(
        <Attachments
          {...commonStepperProps}
          handleCheckboxChange={handleCheckboxChange}
          dataTableDetails={dataTableDetails}
          uploadFileList={uploadFileList}
          onHideAlert={handleHideFileCompatibilityAlert}
          onShowUploadModal={handleShowUploadModal}
          onDeleteFile={handleDeleteFile}
        />,
      )
    : stepperContents;

  return (
    <div className="driver-create-form">
      <div className="dcf-header">
        <div className="dcf-title">
          {input?._id ? t("title.editDriver") : t("title.createDriver")}
        </div>
      </div>
      <div className="dm-divider"></div>
      <form>
        <Stepper activeKey={current} steps={steps}>
          {extendedStepperContents[current]}
        </Stepper>
        <div className="steps-action center mt-50">
          {current > 0 && (
            <Button
              onClick={() => prev()}
              isDisabled={isSubmitLoading}
              type="ghost"
              label={t("action.back")}
              className="mr-10"
            />
          )}
          {current < steps.length - 1 && (
            <Button type="primary" onClick={() => next()} label={t("action.next")} />
          )}
          {current === steps.length - 1 && (
            <Button
              type="primary"
              isLoading={isLoading || isSubmitLoading}
              onClick={() => (input?._id ? handleUpdateSubmit() : handleSubmit())}
              label={t("action.submit")}
            />
          )}
        </div>

        {/* {input._id ? (
          <React.Fragment>
            <div className="main-header">
              <span className="header-title">
                {t("title.id").toUpperCase()} {input.code} | {t("title.activeDriver").toUpperCase()}
              </span>
              <CloseCircleOutlined
                className="cursor-pointer back-action"
                onClick={() => {
                  onBack ? onBack() : {};
                }}
              />
            </div>
            <div>{stepperContents[current]}</div>
            <div className="center mt-50">
              <Button type="primary" label={t("action.save")} onClick={handleUpdateSubmit} />
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="steps-action center mt-50">
              {current > 0 && (
                <Button
                  onClick={() => prev()}
                  type="ghost"
                  label={t("action.back")}
                  className="mr-10"
                />
              )}
              {current < steps.length - 1 && (
                <Button type="primary" onClick={() => next()} label={t("action.next")} />
              )}
              {current === steps.length - 1 && (
                <Button
                  type="primary"
                  isLoading={isLoading}
                  onClick={() => handleSubmit()}
                  label={t("action.submit")}
                />
              )}
            </div>
          </React.Fragment>
        )} */}
      </form>

      {/* MODAL FOR ATTACHMENTS UPLOAD */}
      <BasicModal
        isOpen={uploadModal.open}
        isClosable={false}
        onClose={() => {
          setUploadModal(initialUploadModal);
        }}
        isMaskClosable={false}
        title={uploadModal.title}
        bodyStyle={{ width: "70vw", maxWidth: "500px", margin: "0 auto" }}
      >
        {isShowAlert && <Alert message={t("alert.incompatibleFileFormat")} type="warning" />}
        <Upload
          onCancel={handleCancelUpload}
          onConfirm={() => {
            handleConfirmFile();
          }}
          onChange={(file) => handleFileChange(file, uploadModal.type, uploadModal.format)}
          onRemove={handleFileRemove}
          // onDrop={true}
          fileList={uploadFileList.filter((file) => {
            return file.source === uploadModal.type;
          })}
          fileFormat={uploadModal.format}
          uploadText={
            <div className="p-10">
              <p className="ant-upload-text">{t("txt.uploadImgFile")}</p>
              <p className="ant-upload-text">
                {uploadModal.format === ".pdf" ? t("txt.uploadFormatPDF") : t("txt.uploadFormat")}
              </p>
            </div>
          }
        />
      </BasicModal>
    </div>
  );
};

export default DriverCreateForm;
